import { ref, watch, nextTick, onUnmounted } from "vue";
import { useGuidanceStore } from "../../store/guidance";
import {
  calculateMobileTooltipPosition,
  calculateTooltipPosition,
  mergeOverlappingRects,
  unionAllRects,
} from "../helpers/guidanceHelpers";
import { useBreakpoints } from "./useBreakpoints";

const HIGHLIGHT_PADDING = 8;
const TOOLTIP_GAP = 24;
/** Минимальный отступ окна тура от края экрана. Совпадает с расчётом для телефона. */
const TOOLTIP_EDGE_MARGIN = 12;
// Суммарное время до fallback'а — 1 секунда (изначально было 3, попросили
// сократить дальше). Интервал короче исходных 300мс, чтобы не терять попытки.
const TARGET_RETRY_INTERVAL = 200;
const TARGET_MAX_RETRIES = 5;
// После fallback'а поиск цели не прекращается, а лишь замедляется: грид
// поставщиков в демо (700+ строк) отрисовывается дольше секунды, и при прямом
// проходе шаг 4 оставался без затемнения и подсветки — окно тура висело по
// центру, пока не шагнуть вперёд-назад (CAB-636). Как только цель появляется,
// подсветка встаёт на место сама.
const TARGET_LATE_RETRY_INTERVAL = 500;

export function useGuidanceOverlay() {
  const guidanceStore = useGuidanceStore();
  const { isMobile } = useBreakpoints();

  const targetRects = ref<DOMRect[]>([]);
  const tooltipRef = ref<HTMLElement | null>(null);
  const tooltipSize = ref({ width: 0, height: 0 });
  // Цель шага не нашлась за все попытки (например, удалили все заказы, и
  // чекбокс в шапке таблицы пропал вместе с таблицей) — тур не должен просто
  // пропадать (НФТ отказоустойчивости): оверлей снимается, окно тура
  // остаётся по центру экрана без подсветки.
  const targetNotFound = ref(false);

  let retryTimer = 0;
  let retryCount = 0;
  let resizeObserver: ResizeObserver | null = null;
  let tooltipResizeObserver: ResizeObserver | null = null;
  let rafId = 0;

  function isElementVisible(el: Element): boolean {
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return false;

    // Элемент за левым или правым краем окна — это запаркованная панель,
    // которая ещё не выехала (на телефоне так живёт боковое меню). Размеры у
    // неё настоящие, и без этой проверки подсветка вставала по координатам
    // вроде x = −300, то есть за экраном. Ждём, пока панель приедет.
    //
    // Проверяем только горизонталь: цель ниже сгиба — обычное дело, её
    // подтягивает scrollIntoView перед расчётом рамки.
    const viewportWidth = window.innerWidth;
    if (rect.right <= 0 || rect.left >= viewportWidth) return false;

    return true;
  }

  function findTargetElements(): Element[] {
    const config = guidanceStore.currentStepConfig;
    if (!config?.targetSelector) return [];

    const selectors = Array.isArray(config.targetSelector) ? config.targetSelector : [config.targetSelector];

    const elements: Element[] = [];
    for (const selector of selectors) {
      // Совпадений может быть несколько по двум разным причинам:
      // 1) .single-search__choosing-plugins присутствует в каждой вкладке проценки,
      //    а неактивные скрыты (rect 0×0) — из них видим только активную;
      // 2) карандаш «настроить поставщика» есть в каждой видимой строке таблицы
      //    (ТЕСТ ОНБОРДИНГ 2, шаг «Поставщики») — там видимых сразу несколько,
      //    подсветка рисует рамку на каждую. Оба случая закрывает один и тот же
      //    фильтр по видимости — скрытые (0×0 или за краем экрана) элементы
      //    просто отсеиваются, а не берётся первый попавшийся в DOM.
      const matches = Array.from(document.querySelectorAll(selector));
      elements.push(...matches.filter(isElementVisible));
    }
    return elements;
  }

  // Соседние цели (строка поиска + кнопка «Найти», карандаши в соседних
  // строках, «Оформить»/«Удалить») иначе накладываются рамками друг на друга
  // (ТЕСТ ОНБОРДИНГ 2) — сливаем пересекающиеся (с учётом padding) в один
  // прямоугольник, отдельно стоящие цели (три кнопки-примера) остаются
  // раздельными рамками.
  function computeRects(elements: Element[]): DOMRect[] {
    return mergeOverlappingRects(
      elements.map((el) => el.getBoundingClientRect()),
      HIGHLIGHT_PADDING,
    );
  }

  // Цель может лежать в попапе, который сам донаполняется контентом уже ПОСЛЕ
  // первого успешного измерения (карточка поставщика сперва рисуется маленькой,
  // а через долю секунды разворачивается до полного размера формы) — размер
  // самого document.body при этом не меняется (попап поверх страницы), и
  // resizeObserver на body такое растягивание не ловит. Подсветка залипала на
  // маленьком промежуточном прямоугольнике навсегда. Донаблюдаем каждый найденный
  // элемент отдельно — его собственный ResizeObserver поймает этот донаполнение.
  function observeTargets(elements: Element[]) {
    for (const el of elements) {
      resizeObserver?.observe(el);
    }
  }

  function updateTargetRects() {
    const elements = findTargetElements();
    if (elements.length === 0) {
      targetRects.value = [];
      // Цель могла пропасть уже ПОСЛЕ того, как была найдена — например,
      // виртуальный грид перерисовался и на миг (или насовсем) потерял
      // строку с ней. НФТ отказоустойчивости требует того же поведения, что
      // и при изначальном ненахождении: повторные попытки, а затем fallback
      // по центру. Не перезапускаем цикл, если он и так уже идёт (retryTimer)
      // или уже завершился отказом (targetNotFound) — иначе шквал
      // resize/scroll событий держал бы retryCount вечно на нуле, и fallback
      // никогда бы не наступил (см. регресс с зависающим оверлеем при
      // удалении заказов).
      if (!retryTimer && !targetNotFound.value) {
        startRetryingTarget();
      }
      return;
    }
    targetNotFound.value = false;
    targetRects.value = computeRects(elements);
    observeTargets(elements);
  }

  function updateTooltipSize() {
    if (tooltipRef.value) {
      tooltipSize.value = {
        width: tooltipRef.value.offsetWidth,
        height: tooltipRef.value.offsetHeight,
      };
    }
  }

  function scheduleUpdate() {
    cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(() => {
      updateTargetRects();
      nextTick(updateTooltipSize);
    });
  }

  function setupObservers() {
    cleanupObservers();

    resizeObserver = new ResizeObserver(scheduleUpdate);
    resizeObserver.observe(document.body);

    window.addEventListener("resize", scheduleUpdate);
    window.addEventListener("scroll", scheduleUpdate, true);
  }

  function cleanupObservers() {
    resizeObserver?.disconnect();
    resizeObserver = null;
    tooltipResizeObserver?.disconnect();
    tooltipResizeObserver = null;
    window.removeEventListener("resize", scheduleUpdate);
    window.removeEventListener("scroll", scheduleUpdate, true);
    cancelAnimationFrame(rafId);
  }

  function startRetryingTarget() {
    stopRetrying();
    retryCount = 0;
    targetNotFound.value = false;

    const tryFind = () => {
      const elements = findTargetElements();
      if (elements.length > 0) {
        stopRetrying();
        targetNotFound.value = false;
        // Подсвечиваемый элемент может быть проскроллен за пределы своего контейнера
        // (например, кнопка «Проценка» в удлинившемся сайдбаре). Подтягиваем его в
        // видимую область до расчёта рамки — иначе спотлайт встанет не на ту кнопку.
        elements[0].scrollIntoView({ block: "nearest", inline: "nearest" });
        rafId = requestAnimationFrame(() => {
          targetRects.value = computeRects(elements);
          nextTick(updateTooltipSize);
          setupObservers();
          observeTargets(elements);
        });
        return;
      }
      retryCount++;
      if (retryCount < TARGET_MAX_RETRIES) {
        retryTimer = window.setTimeout(tryFind, TARGET_RETRY_INTERVAL);
      } else {
        targetNotFound.value = true;
        retryTimer = window.setTimeout(tryFind, TARGET_LATE_RETRY_INTERVAL);
      }
    };

    tryFind();
  }

  function stopRetrying() {
    if (retryTimer) {
      clearTimeout(retryTimer);
      retryTimer = 0;
    }
  }

  /**
   * Докуда подсказке можно опускаться на телефоне: до верхнего края кружка с
   * номером шага. Замеряем его, а не закладываем число: кружок смещается на
   * устройствах с «домашней полоской», и любая константа рано или поздно врёт.
   */
  function mobileBottomLimit(): number {
    const badge = document.querySelector(".guidance-floating-button");
    const viewportHeight = window.innerHeight;
    if (!badge) return viewportHeight - 12;

    const rect = badge.getBoundingClientRect();
    if (rect.height <= 0) return viewportHeight - 12;

    return rect.top - TOOLTIP_GAP / 2;
  }

  /**
   * Докуда окну тура можно опускаться на шаге "center-bottom": фиксированный
   * отступ от нижнего края viewport, без привязки к футеру попапа — футер
   * может оказаться в середине экрана (короткая карточка), и тогда окно тура
   * уезжало бы туда же, а не к низу экрана.
   */
  function centerBottomLimit(): number {
    return window.innerHeight - 16;
  }

  function getTooltipPosition() {
    if (targetRects.value.length === 0) {
      if (!targetNotFound.value) return null;

      const tw = tooltipSize.value.width ?? 340;
      const th = tooltipSize.value.height ?? 220;
      return { x: window.innerWidth / 2 - tw / 2, y: window.innerHeight / 2 - th / 2 };
    }

    const config = guidanceStore.currentStepConfig;
    if (!config) return null;

    // Позиция считается от охватывающего прямоугольника ВСЕХ целей шага, а не
    // только первой (targetRects[0]) — на шагах с несколькими раздельными
    // целями (строка поиска + кнопки-примеры под ней) фолбэк "bottom" иначе
    // вставал сразу под первой целью, перекрывая остальные, лежащие ниже
    // (CAB-636, замечание 1).
    const boundingRect = unionAllRects(targetRects.value);
    const tw = tooltipSize.value.width ?? 340;
    const th = tooltipSize.value.height ?? 220;

    // Карточка поставщика (шаг 4 онбординга), развёрнутая на полную высоту —
    // футер с кнопкой «Проверить» стоит вплотную к низу попапа, свободного
    // места под целью для computePosition("bottom") не остаётся, и окно тура
    // клампится прямо на кнопку. Фиксируем окно по центру снизу экрана вместо
    // расчёта от прямоугольника цели — так оно никогда не наезжает на футер.
    if (config.tooltipPosition === "center-bottom") {
      // На телефоне окно тура во всю ширину и потому высокое: на коротком экране
      // «центр снизу» уводил его выше верхнего края, и «Далее» становилась
      // недостижимой — прокрутки у страницы под оверлеем нет. Ограничиваем так
      // же, как мобильный расчёт: не выше отступа от края.
      const x = window.innerWidth / 2 - tw / 2;
      const y = Math.max(TOOLTIP_EDGE_MARGIN, centerBottomLimit() - th);
      return { x, y: avoidTargets(x, y, tw, th, boundingRect) };
    }

    // На телефоне подсказка почти во всю ширину и сбоку от элемента не
    // помещается — она накрывала бы то, на что показывает.
    const pos = isMobile.value
      ? calculateMobileTooltipPosition(boundingRect, th, HIGHLIGHT_PADDING, TOOLTIP_GAP, mobileBottomLimit())
      : calculateTooltipPosition(
          boundingRect,
          tw,
          th,
          config.tooltipPosition ?? "bottom",
          HIGHLIGHT_PADDING,
          TOOLTIP_GAP,
        );

    // Точечная поправка (например, шаг 2 онбординга — окно чуть ниже на 25px).
    if (config.tooltipOffsetY) {
      return { ...pos, y: pos.y + config.tooltipOffsetY };
    }
    return pos;
  }

  /**
   * Короткая карточка (демо-поставщик без полей) стоит посреди экрана, и окно
   * тура «по центру снизу» ложилось прямо на её кнопки «Проверить»/«Сохранить» —
   * на то, что само же подсвечивает (CAB-636). Если окно накрывает цели, уводим
   * его под них, а нет места — над них; иначе оставляем как есть.
   */
  function avoidTargets(x: number, y: number, tw: number, th: number, targets: DOMRect): number {
    const overlapsX = x < targets.right + HIGHLIGHT_PADDING && x + tw > targets.left - HIGHLIGHT_PADDING;
    const overlapsY = y < targets.bottom + HIGHLIGHT_PADDING && y + th > targets.top - HIGHLIGHT_PADDING;
    if (!overlapsX || !overlapsY) return y;

    const below = targets.bottom + HIGHLIGHT_PADDING + TOOLTIP_GAP;
    if (below + th <= centerBottomLimit()) return below;

    const above = targets.top - HIGHLIGHT_PADDING - TOOLTIP_GAP - th;
    if (above >= TOOLTIP_EDGE_MARGIN) return above;

    return y;
  }

  function getArrowPoints() {
    if (targetRects.value.length === 0) return null;

    const config = guidanceStore.currentStepConfig;
    if (!config) return null;

    const rect = targetRects.value[0];
    const pos = config.tooltipPosition;
    const tp = getTooltipPosition();
    if (!tp) return null;

    const tw = tooltipSize.value.width ?? 340;
    const th = tooltipSize.value.height ?? 220;

    let fromX = 0;
    let fromY = 0;

    if (pos === "bottom") {
      fromX = rect.left + rect.width / 2;
      fromY = rect.bottom + HIGHLIGHT_PADDING;
    } else if (pos === "right") {
      fromX = rect.right + HIGHLIGHT_PADDING;
      fromY = rect.top + rect.height / 2;
    } else {
      fromX = rect.left - HIGHLIGHT_PADDING;
      fromY = rect.top + rect.height / 2;
    }

    const inset = 14;
    let toX = 0;
    let toY = 0;

    if (pos === "bottom") {
      toX = tp.x + tw * 0.3;
      toY = tp.y + inset;
    } else if (pos === "right") {
      toX = tp.x + inset;
      toY = tp.y + th * 0.3;
    } else {
      toX = tp.x + tw - inset;
      toY = tp.y + th * 0.3;
    }

    return { fromX, fromY, toX, toY };
  }

  function getClickTargetStyle(): Record<string, string> | null {
    if (targetRects.value.length === 0) return null;

    const rect = targetRects.value[0];
    return {
      position: "absolute",
      left: `${rect.left - HIGHLIGHT_PADDING}px`,
      top: `${rect.top - HIGHLIGHT_PADDING}px`,
      width: `${rect.width + HIGHLIGHT_PADDING * 2}px`,
      height: `${rect.height + HIGHLIGHT_PADDING * 2}px`,
      zIndex: "10001",
      cursor: "pointer",
      borderRadius: "8px",
    };
  }

  watch(
    () => guidanceStore.currentStepConfig,
    async (step) => {
      stopRetrying();
      cleanupObservers();
      targetRects.value = [];
      targetNotFound.value = false;

      if (step) {
        await nextTick();
        startRetryingTarget();
      }
    },
    { immediate: true },
  );

  watch(tooltipRef, (el) => {
    tooltipResizeObserver?.disconnect();
    tooltipResizeObserver = null;

    if (el) {
      tooltipResizeObserver = new ResizeObserver(() => {
        updateTooltipSize();
      });
      tooltipResizeObserver.observe(el);
    }
  });

  onUnmounted(() => {
    stopRetrying();
    cleanupObservers();
  });

  return {
    targetRects,
    tooltipRef,
    tooltipSize,
    targetNotFound,
    highlightPadding: HIGHLIGHT_PADDING,
    getTooltipPosition,
    getArrowPoints,
    getClickTargetStyle,
    updateTooltipSize,
  };
}
